import type {
  GetUserAnalyticsOrderByParamADto,
  UserAnalyticsADto,
} from '@cohort/admin-schemas/userAnalytics';
import {IconBadge} from '@cohort/merchants/components/IconBadge';
import Table from '@cohort/merchants/components/Table';
import {getUserRoute} from '@cohort/merchants/lib/Pages';
import {formatDate} from '@cohort/shared/utils/format';
import {Flag, Gift, Stack} from '@phosphor-icons/react';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

interface UsersTableProps {
  users: UserAnalyticsADto[];
  isLoading: boolean;
  orderBy: GetUserAnalyticsOrderByParamADto;
  setOrderBy: (orderBy: GetUserAnalyticsOrderByParamADto) => void;
}

const UsersTable: React.FC<UsersTableProps> = ({users, isLoading, orderBy, setOrderBy}) => {
  const {t} = useTranslation('pages', {keyPrefix: 'users.overview.usersTable'});
  const navigate = useNavigate();

  const headers = [
    {label: t('tableHeaderEmail')},
    {label: t('tableHeaderMemberHandle')},
    {label: t('tableHeaderJoinedAt'), sortingKey: 'joinedAt'},
    {label: t('tableHeaderLastActiveAt'), sortingKey: 'lastActiveAt'},
    {label: t('tableHeaderJourneys'), sortingKey: 'countCompletedJourneys'},
    {label: t('tableHeaderDigitalAssets'), sortingKey: 'countDigitalAssets'},
    {label: t('tableHeaderPerks'), sortingKey: 'countUsedPerks'},
  ];

  return (
    <Table
      headers={headers}
      isLoading={isLoading}
      emptyState={{
        isEmpty: users.length === 0,
        title: t('titleEmpty'),
        description: t('descriptionEmpty'),
      }}
      sortBy={orderBy}
      onSort={orderBy => setOrderBy(orderBy as GetUserAnalyticsOrderByParamADto)}
    >
      {users.map(user => (
        <tr
          key={user.id}
          className="h-14 cursor-pointer text-sm hover:bg-slate-50"
          onClick={() => navigate(getUserRoute(user.id).path)}
        >
          <td className="pr-1 font-semibold">{user.email}</td>
          <td className="pr-1 text-slate-500">{user.membershipPassHandle}</td>
          <td className="py-3 pr-1 text-slate-500">{formatDate(user.joinedAt)}</td>
          <td className="py-3 pr-1 text-slate-500">{formatDate(user.lastActiveAt)}</td>
          <td>
            <IconBadge
              icon={<Flag className="mr-2 h-4 w-4 text-slate-400" />}
              text={`${user.countCompletedJourneys}/${user.countJourneys}`}
            />
          </td>
          <td className="pr-1 font-medium">
            <IconBadge
              icon={<Stack className="mr-2 h-4 w-4 text-slate-400" />}
              text={`${user.countDigitalAssets}`}
            />
          </td>
          <td>
            <IconBadge
              icon={<Gift className="mr-2 h-4 w-4 text-slate-400" />}
              text={`${user.countUsedPerks}/${user.countPerks}`}
            />
          </td>
        </tr>
      ))}
    </Table>
  );
};

export default UsersTable;
